// "Which One Piece Character Are You?" — personality quiz.
// Pick answers, points tally to a character, show the result + share.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import QuizPageText from '../components/QuizPageText/QuizPageText';
import { whichCharacterText } from '../components/QuizPageText/quizPageText.data';
import { QUESTIONS, RESULTS } from '../data/characterMatchQuiz';
import { recordPlay } from '../services/playHistory';
import GuestNudge from '../components/GuestNudge/GuestNudge';
import './WhichCharacter.css';
import useGameFullscreen from '../hooks/useGameFullscreen';

const WhichCharacter = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);              // current question index
  const [scores, setScores] = useState({});         // characterKey -> points
  const [result, setResult] = useState(null);       // result character key
  useGameFullscreen(!result);
  const [copied, setCopied] = useState(false);

  const total = QUESTIONS.length;

  const pick = (option) => {
    const next = { ...scores };
    option.scores.forEach((key) => {
      next[key] = (next[key] || 0) + 1;
    });
    setScores(next);

    if (step + 1 < total) {
      setStep(step + 1);
    } else {
      // Ties used to fall to whichever character came first in RESULTS, and that
      // is Luffy — so he won every tie and came out 32% of the time on random
      // answers. Pick at random among everyone on the top score instead.
      const best = Math.max(...Object.keys(RESULTS).map((k) => next[k] || 0));
      const leaders = Object.keys(RESULTS).filter((k) => (next[k] || 0) === best);
      const picked = leaders[Math.floor(Math.random() * leaders.length)];
      setResult(picked);
      recordPlay({ gameType: 'which', result: RESULTS[picked].name, image: RESULTS[picked].image });
    }
  };

  const restart = () => {
    setStep(0);
    setScores({});
    setResult(null);
    setCopied(false);
  };

  const handleShare = async () => {
    const r = RESULTS[result];
    const text = `I'm ${r.name} — ${r.tagline}! Which One Piece character are you? https://www.onepiecetrivia.com/which-character`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Which One Piece Character Are You?', text });
        return;
      }
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      /* user cancelled share — ignore */
    }
  };

  return (
    <>
      <Helmet>
        <title>Which One Piece Character Are You? | One Piece Trivia</title>
        <meta name="description" content="Take the personality quiz and find out which One Piece character matches you — Luffy, Zoro, Nami, Law and more." />
        <link rel="canonical" href="https://www.onepiecetrivia.com/which-character" />
        <meta property="og:title" content="Which One Piece Character Are You?" />
        <meta property="og:description" content="Eight questions about what you want and how you fight, and one of twelve characters comes out." />
        <meta property="og:url" content="https://www.onepiecetrivia.com/which-character" />
      </Helmet>

      <div className="wc-container">
        {!result ? (
          <div className="wc-card">
            {step === 0 && <span className="not-on-board-note">Not yet in the leaderboard</span>}
            <h1 className="wc-title">Which One Piece Character Are You?</h1>

            <div className="wc-progress">
              <div className="wc-progress-bar" style={{ width: `${(step / total) * 100}%` }} />
            </div>
            <p className="wc-step-count">Question {step + 1} of {total}</p>

            <h2 className="wc-question">{QUESTIONS[step].q}</h2>

            <div className="wc-options">
              {QUESTIONS[step].options.map((option, i) => (
                <button key={i} className="wc-option" onClick={() => pick(option)}>
                  {option.text}
                </button>
              ))}
            </div>

            {step > 0 && (
              <button type="button" className="wc-restart" onClick={restart}>
                Start again
              </button>
            )}
          </div>
        ) : (
          <div className="wc-card wc-result">
            <p className="wc-result-label">You are...</p>
            <img src={RESULTS[result].image} alt={RESULTS[result].name} className="wc-result-img" />
            <h1 className="wc-result-name">{RESULTS[result].name}</h1>
            <p className="wc-result-tagline">{RESULTS[result].tagline}</p>
            <p className="wc-result-blurb">{RESULTS[result].blurb}</p>

            <div className="wc-actions">
              <button className="wc-btn wc-btn-primary" onClick={handleShare}>
                {copied ? '✓ Copied!' : '🏴‍☠️ Share your result'}
              </button>
              <button className="wc-btn" onClick={() => navigate(`/characters/${RESULTS[result].slug}`)}>
                See {RESULTS[result].name.split(' ')[0]}'s profile
              </button>
              <button className="wc-btn wc-btn-ghost" onClick={restart}>
                Try again
              </button>
            </div>

            <GuestNudge compact />

            <button className="wc-play-quiz" onClick={() => navigate('/quiz')}>
              Now test your knowledge → Play the quiz
            </button>
          </div>
        )}
      </div>
      <QuizPageText {...whichCharacterText} />
    </>
  );
};

export default WhichCharacter;
